// What a message may carry, asked of the api once per session and side.
//
// NULL HIDES EVERY ATTACH CONTROL. The rules arrive in the inbox's `meta`, and
// only an api that takes files sends them. A web deployed ahead of that api
// would otherwise offer a picker whose files the old api ignores — the message
// sent, the screenshot silently gone, and the client sure it arrived.
//
// THE LIMITS ARE THE SERVER'S, read from it rather than copied here. These
// checks only save a round trip; the server makes the same ones and is the
// one that decides.

import { useEffect, useState } from 'react';
import { AttachmentRules } from '../../../types/messagingApi';
import { clientMessagingService, messagingService } from '../../../services/messagingService';
import { getGlobalBusinessId } from '../../../utils/apiUtils';

const cache = new Map<string, Promise<AttachmentRules | null>>();

/** Tests only: each test starts with nothing asked. */
export function resetAttachmentRulesCache(): void {
  cache.clear();
}

function useRules(key: string | null, load: () => Promise<AttachmentRules | null>) {
  const [rules, setRules] = useState<AttachmentRules | null>(null);
  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    let pending = cache.get(key);
    if (!pending) {
      // A failure is forgotten, so the next screen asks again rather than
      // hiding attachments for the rest of the session.
      // Through a `then`, so a loader that throws synchronously is a refusal
      // like any other rather than an error thrown out of an effect.
      pending = Promise.resolve().then(load).catch(() => {
        cache.delete(key);
        return null;
      });
      cache.set(key, pending);
    }
    pending.then(value => { if (live) setRules(value); });
    return () => { live = false; };
    // `load` is a fresh closure every render; the key says what it loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return rules;
}

/**
 * Asked only once a business is active — the client's inbox is business-scoped,
 * and asking without one throws. Read each render, so the nav (mounted above
 * the business shell) asks as soon as a business is chosen.
 */
export function useClientAttachmentRules(enabled = true): AttachmentRules | null {
  const businessId = getGlobalBusinessId();
  return useRules(enabled && businessId ? `client:${businessId}` : null,
                  () => clientMessagingService.attachmentRules());
}

export function useFirmAttachmentRules(firmId: string | null | undefined): AttachmentRules | null {
  return useRules(firmId ? `firm:${firmId}` : null, () => messagingService.attachmentRules(firmId!));
}

export function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
}

/** Same three refusals the server makes, said before the upload instead of after. */
export function addFiles(rules: AttachmentRules, current: File[], incoming: File[]):
  { files: File[]; error: string | null } {
  const files = [...current, ...incoming];
  if (files.length > rules.max_files) {
    return { files: current, error: `You can attach up to ${rules.max_files} files to one message.` };
  }
  const tooBig = incoming.find(file => file.size > rules.max_file_bytes);
  if (tooBig) {
    return { files: current,
             error: `${tooBig.name} is over ${humanBytes(rules.max_file_bytes)}.` };
  }
  const empty = incoming.find(file => file.size === 0);
  if (empty) return { files: current, error: `${empty.name} is empty.` };
  if (files.reduce((sum, file) => sum + file.size, 0) > rules.max_total_bytes) {
    return { files: current,
             error: `Files on one message must add up to ${humanBytes(rules.max_total_bytes)} or less.` };
  }
  return { files, error: null };
}

export const isPreviewableImage = (contentType: string): boolean =>
  ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(contentType);
